import React from "react";
import { Navigate, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

/**
 * ProtectedRoute
 *
 * Usage in your router:
 *
 *   <Route element={<ProtectedRoute allowedRoles={["patient"]} />}>
 *     <Route path="/patient" element={<PatientDashboard />} />
 *   </Route>
 *
 * Or wrap a single component:
 *   <Route
 *     path="/admin"
 *     element={
 *       <ProtectedRoute allowedRoles={["admin"]}>
 *         <AdminDashboard />
 *       </ProtectedRoute>
 *     }
 *   />
 */
export default function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();

  // ✅ KEY FIX: wait for localStorage restore before making any redirect decision.
  // Without this, user is null for a brief moment on page load → guard redirects
  // to /login → looks like an automatic reload / logout loop.
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-teal-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Loading…</p>
        </div>
      </div>
    );
  }

  // Not logged in → send to login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Logged in but wrong role → send to their own dashboard
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    const fallbackMap = {
      admin: "/admin",
      doctor: "/doctor",
      patient: "/patient",
      lab: "/lab",
    };
    return <Navigate to={fallbackMap[user.role] || "/"} replace />;
  }

  // All good — render children (direct wrap) or nested routes (Outlet)
  return children ? children : <Outlet />;
}